import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BrandLogo } from "@/components/BrandLogo";
import { I } from "@/components/design";
import { NavItem } from "@/components/layout/NavItem";
import { UserMenu } from "@/components/layout/UserMenu";
import { MerchantSwitcher } from "@/components/MerchantSwitcher";
import { useAttentionBadge } from "@/hooks/useAttentionQueue";
import { useProfile } from "@/hooks/useProfile";
import { space } from "@/theme/foundation";

interface NavEntry {
  key: string;
  /** Catalogue key — resolved at render so the sidebar follows the language. */
  labelKey: string;
  icon: ReactElement;
}

const HOME_NAV_ITEM: NavEntry = { key: "/dashboard", labelKey: "common:nav.home", icon: <I.home size={15} /> };
const INBOX_NAV_ITEM: NavEntry = { key: "/chat", labelKey: "common:nav.chat", icon: <I.inbox size={15} /> };

const WORK_NAV_ITEMS: NavEntry[] = [
  HOME_NAV_ITEM,
  INBOX_NAV_ITEM,
  { key: "/crm", labelKey: "common:nav.crm", icon: <I.users size={15} /> },
  { key: "/orders", labelKey: "common:nav.orders", icon: <I.receipt size={15} /> },
  { key: "/products", labelKey: "common:nav.products", icon: <I.box size={15} /> },
];

const TEAM_NAV_ITEM: NavEntry = { key: "/team", labelKey: "common:nav.team", icon: <I.user size={15} /> };

const SETTINGS_NAV_ITEM: NavEntry = {
  key: "/settings",
  labelKey: "common:nav.channels",
  icon: <I.cog size={15} />,
};

const BILLING_NAV_ITEM: NavEntry = {
  key: "/billing",
  labelKey: "common:nav.billing",
  icon: <I.card size={15} />,
};

interface SidebarProps {
  pathname: string;
  onNavigate?: () => void;
}

function matchesPath(current: string, target: string): boolean {
  return current === target || current.startsWith(`${target}/`);
}

/** Whether `entry` should highlight for the current path. A parent entry
 *  (e.g. `/settings`) yields to a more-specific sibling nav item
 *  (e.g. `/settings/ai`) that owns the current path, so only one item is
 *  active at a time. Non-nav subroutes (e.g. `/settings/channels`) still
 *  light up their nearest nav parent. */
function isActiveEntry(current: string, entry: NavEntry, allEntries: NavEntry[]): boolean {
  if (!matchesPath(current, entry.key)) return false;
  const claimedByDeeper = allEntries.some(
    (other) => other.key !== entry.key && other.key.startsWith(`${entry.key}/`) && matchesPath(current, other.key),
  );
  return !claimedByDeeper;
}

const AI_NAV_ITEM: NavEntry = {
  key: "/settings/ai",
  labelKey: "common:nav.aiAssistant",
  icon: <I.zap size={15} />,
};

const DELIVERY_NAV_ITEM: NavEntry = {
  key: "/settings/delivery",
  labelKey: "common:nav.delivery",
  icon: <I.truck size={15} />,
};

export function Sidebar({ pathname, onNavigate }: SidebarProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile, canManageBilling, canManageAiBot, canConnectPlatform, canManageOrders } = useProfile();

  // Chats waiting for a person, read from one short page of the queue, so a
  // longer queue shows as "5+".
  const inboxBadge = useAttentionBadge();

  // Daily work first; shop setup is permission-gated and rarely visited.
  let setupItems = [TEAM_NAV_ITEM];
  if (canConnectPlatform) setupItems = [...setupItems, SETTINGS_NAV_ITEM];
  if (canManageAiBot) setupItems = [...setupItems, AI_NAV_ITEM];
  if (canManageOrders) setupItems = [...setupItems, DELIVERY_NAV_ITEM];
  // Receipt details are edited on the billing page itself (ReceiptProfileCard),
  // next to the checkout that consumes them — not as their own nav entry.
  if (canManageBilling) setupItems = [...setupItems, BILLING_NAV_ITEM];
  const allItems = [...WORK_NAV_ITEMS, ...setupItems];
  const groups = [
    { id: "work", labelKey: "common:nav.groupWork", items: WORK_NAV_ITEMS },
    { id: "setup", labelKey: "common:nav.groupSetup", items: setupItems },
  ];

  function go(href: string) {
    navigate(href);
    onNavigate?.();
  }

  return (
    <nav
      aria-label={t("common:nav.mainAria")}
      className="flex min-h-full flex-col justify-between gap-6"
      style={{ background: "var(--soft-cloud)", padding: "var(--space-4) var(--space-3)" }}
    >
      <div className="col gap-4">
        <div className="row gap-2.5" style={{ alignItems: "center", padding: "0 var(--space-3)" }}>
          <BrandLogo size={26} />
          {profile?.memberships?.length ? (
            <MerchantSwitcher />
          ) : (
            <div className="sidebar-trigger" aria-disabled="true">
              <span className="sidebar-trigger-label">Sight Order</span>
            </div>
          )}
        </div>

        {groups.map((group) => (
          <div key={group.id} className="nav-group">
            <span className="nav-group-label">{t(group.labelKey)}</span>
            {group.items.map((entry) => (
              <NavItem
                key={entry.key}
                icon={entry.icon}
                label={t(entry.labelKey)}
                to={entry.key}
                isActive={isActiveEntry(pathname, entry, allItems)}
                onClick={() => go(entry.key)}
                badge={entry === INBOX_NAV_ITEM ? inboxBadge?.text : undefined}
                badgeLabel={entry === INBOX_NAV_ITEM ? inboxBadge?.label : undefined}
              />
            ))}
          </div>
        ))}
      </div>

      <div
        className="row"
        style={{
          borderTop: "1px solid var(--hairline)",
          paddingTop: space[4],
          justifyContent: "flex-start",
          paddingLeft: space[2],
        }}
      >
        <UserMenu />
      </div>
    </nav>
  );
}
